import type { ReactNode } from 'react';

import { cn } from '@shared/utils/cn';

export type StatusLabelColor = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'black';
type Variant = 'soft' | 'filled' | 'outlined';

// Remplace le `Label` de Minimal (24px, rayon 6px, 12px bold)
const STYLES: Record<Variant, Record<StatusLabelColor, string>> = {
  soft: {
    default: 'bg-muted-foreground/16 text-foreground',
    primary: 'bg-primary/16 text-primary',
    secondary: 'bg-foreground/12 text-foreground',
    info: 'bg-info/16 text-info',
    success: 'bg-success/16 text-success',
    warning: 'bg-warning/16 text-warning',
    error: 'bg-destructive/16 text-destructive',
    black: 'bg-foreground/16 text-foreground',
  },
  filled: {
    default: 'bg-muted-foreground text-white',
    primary: 'bg-primary text-primary-foreground',
    secondary: 'bg-foreground/80 text-background',
    info: 'bg-info text-white',
    success: 'bg-success text-white',
    warning: 'bg-warning text-white',
    error: 'bg-destructive text-white',
    black: 'bg-foreground text-background',
  },
  outlined: {
    default: 'border border-muted-foreground/48 text-foreground',
    primary: 'border border-primary text-primary',
    secondary: 'border border-foreground/48 text-foreground',
    info: 'border border-info text-info',
    success: 'border border-success text-success',
    warning: 'border border-warning text-warning',
    error: 'border border-destructive text-destructive',
    black: 'border border-foreground text-foreground',
  },
};

type Props = {
  children?: ReactNode;
  color?: StatusLabelColor;
  variant?: Variant;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  className?: string;
};

export const StatusLabel = ({
  children,
  color = 'default',
  variant = 'soft',
  startIcon,
  endIcon,
  className,
}: Props) => (
  <span
    className={cn(
      'inline-flex h-6 min-w-6 shrink-0 items-center justify-center gap-1.5 rounded-md px-1.5 text-xs font-bold whitespace-nowrap [&>svg]:size-3.5',
      STYLES[variant][color],
      className
    )}
    data-slot="status-label"
  >
    {startIcon}
    {children}
    {endIcon}
  </span>
);
